// Typescript CodeGen Prototype "Expectant Sunshine"
// Transform Version: v1.0.0
// Template Version:  v1.0.0
//
// -- DO NOT MODIFY THIS FILE! --

/* tslint:disable:no-trailing-whitespace */

import * as _ from 'lodash';
import * as Wellspring from '@greenheck/wellspring';
import * as Axon from '@jlekie/axon';
import { v4 as Uuid } from 'uuid';
import * as ParseHelpers from '@jlekie/parse-helpers';
import * as Zod from 'zod';
import * as ConfigurationCore from './configuration.core';
import * as ConfigurationModelEntity from './configuration.modelEntity';

export enum Applications {
    Intake,
    Exhaust
}

export enum SelectionMethods {
    VolumePressure,
    VolumePressureWidth,
    VolumePressureHeight,
    VolumeVelocity,
    VolumeVelocityWidth,
    VolumeVelocityHeight,
    VolumeWidthHeight,
    PressureWidthHeight,
    WidthHeight,
    FreeArea
}

export enum Capacities {
    NoPreference,
    ClassA29_3,
    ClassA50_8,
    BPWP
}

export enum FinishTypes {
    None,
    Kynar_70,
    Kynar3Coat,
    Kynar4Coat,
    Acroflur,
    Anodize,
    BakedEnamel,
    AcrylicEnamel,
    Primer,
    Mica,
    Pearlecent,
    Epoxy,
    HiProPolyester,
    WoodGrain
}

export function isSelectorModel(source: any): source is ISelector {
    // Method Implementation: simple
    return source.modelName === 'Selector';
}

export function isSelectionModel(source: any): source is ISelection {
    // Method Implementation: simple
    return source.modelName === 'Selection';
}

export function isValidSizeModel(source: any): source is IValidSize {
    // Method Implementation: simple
    return source.modelName === 'ValidSize';
}

export interface SelectorZodSchemaShape {
    application?: 'Intake' | 'Exhaust';

    selectionMethod?: 'VolumePressure' | 'VolumePressureWidth' | 'VolumePressureHeight' | 'VolumeVelocity' | 'VolumeVelocityWidth' | 'VolumeVelocityHeight' | 'VolumeWidthHeight' | 'PressureWidthHeight' | 'WidthHeight' | 'FreeArea';

    minimumFreeArea?: number;

    width?: number;

    height?: number;

    isDamperIncluded?: boolean;

    model?: string;

    overrideLimits?: boolean;

    pressureDrop?: number;

    velocity?: number;

    volume?: number;

    waterPenetrationSafetyFactor?: number;

    widthIncrement?: number;

    heightIncrement?: number;

    capacity?: 'NoPreference' | 'ClassA29_3' | 'ClassA50_8' | 'BPWP';

    finish?: 'None' | 'Kynar_70' | 'Kynar3Coat' | 'Kynar4Coat' | 'Acroflur' | 'Anodize' | 'BakedEnamel' | 'AcrylicEnamel' | 'Primer' | 'Mica' | 'Pearlecent' | 'Epoxy' | 'HiProPolyester' | 'WoodGrain';
}

export interface SelectionZodSchemaShape {
    sizes?: ValidSizeZodSchemaShape[];
}

export interface ValidSizeZodSchemaShape {
    name?: string;

    freeArea?: number;

    pressureDrop?: number;

    velocity?: number;

    volume?: number;
}

// Artifact Implementation: artifacts/zodSchemaType
export const SelectorZodSchema: Zod.ZodSchema<SelectorZodSchemaShape> = Zod.lazy(() => Zod.object({
    application: Zod.enum([ 'Intake', 'Exhaust' ]).optional(),
    selectionMethod: Zod.enum([ 'VolumePressure', 'VolumePressureWidth', 'VolumePressureHeight', 'VolumeVelocity', 'VolumeVelocityWidth', 'VolumeVelocityHeight', 'VolumeWidthHeight', 'PressureWidthHeight', 'WidthHeight', 'FreeArea' ]).optional(),
    minimumFreeArea: Zod.number().optional(),
    width: Zod.number().optional(),
    height: Zod.number().optional(),
    isDamperIncluded: Zod.boolean().optional(),
    model: Zod.string().optional(),
    overrideLimits: Zod.boolean().optional(),
    pressureDrop: Zod.number().optional(),
    velocity: Zod.number().optional(),
    volume: Zod.number().optional(),
    waterPenetrationSafetyFactor: Zod.number().optional(),
    widthIncrement: Zod.number().optional(),
    heightIncrement: Zod.number().optional(),
    capacity: Zod.enum([ 'NoPreference', 'ClassA29_3', 'ClassA50_8', 'BPWP' ]).optional(),
    finish: Zod.enum([ 'None', 'Kynar_70', 'Kynar3Coat', 'Kynar4Coat', 'Acroflur', 'Anodize', 'BakedEnamel', 'AcrylicEnamel', 'Primer', 'Mica', 'Pearlecent', 'Epoxy', 'HiProPolyester', 'WoodGrain' ]).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const SelectionZodSchema: Zod.ZodSchema<SelectionZodSchemaShape> = Zod.lazy(() => Zod.object({
    sizes: Zod.array(ValidSizeZodSchema).optional()
}).nonstrict());

// Artifact Implementation: artifacts/zodSchemaType
export const ValidSizeZodSchema: Zod.ZodSchema<ValidSizeZodSchemaShape> = Zod.lazy(() => Zod.object({
    name: Zod.string().optional(),
    freeArea: Zod.number().optional(),
    pressureDrop: Zod.number().optional(),
    velocity: Zod.number().optional(),
    volume: Zod.number().optional()
}).nonstrict());

export interface ISelector extends Wellspring.IModel {
    readonly modelName: string;

    readonly application: Applications;

    readonly selectionMethod: SelectionMethods;

    readonly minimumFreeArea: number;

    readonly width: number;

    readonly height: number;

    readonly isDamperIncluded: boolean;

    readonly model: string;

    readonly overrideLimits: boolean;

    readonly pressureDrop: number;

    readonly velocity: number;

    readonly volume: number;

    readonly waterPenetrationSafetyFactor: number;

    readonly widthIncrement: number;

    readonly heightIncrement: number;

    readonly capacity: Capacities;

    readonly finish: FinishTypes;

    readonly isApplicationDefined: boolean;

    readonly isSelectionMethodDefined: boolean;

    readonly isMinimumFreeAreaDefined: boolean;

    readonly isWidthDefined: boolean;

    readonly isHeightDefined: boolean;

    readonly isIsDamperIncludedDefined: boolean;

    readonly isModelDefined: boolean;

    readonly isOverrideLimitsDefined: boolean;

    readonly isPressureDropDefined: boolean;

    readonly isVelocityDefined: boolean;

    readonly isVolumeDefined: boolean;

    readonly isWaterPenetrationSafetyFactorDefined: boolean;

    readonly isWidthIncrementDefined: boolean;

    readonly isHeightIncrementDefined: boolean;

    readonly isCapacityDefined: boolean;

    readonly isFinishDefined: boolean;
}

export interface ISelection extends Wellspring.IModel {
    readonly modelName: string;

    readonly sizes: ValidSize[];

    readonly isSizesDefined: boolean;
}

export interface IValidSize extends Wellspring.IModel {
    readonly modelName: string;

    readonly name: string;

    readonly freeArea: number;

    readonly pressureDrop: number;

    readonly velocity: number;

    readonly volume: number;

    readonly isNameDefined: boolean;

    readonly isFreeAreaDefined: boolean;

    readonly isPressureDropDefined: boolean;

    readonly isVelocityDefined: boolean;

    readonly isVolumeDefined: boolean;
}

export class Selector implements ISelector {
    public static readonly modelName: string = 'Selector';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'application', 'selectionMethod', 'minimumFreeArea', 'width', 'height', 'isDamperIncluded', 'model', 'overrideLimits', 'pressureDrop', 'velocity', 'volume', 'waterPenetrationSafetyFactor', 'widthIncrement', 'heightIncrement', 'capacity', 'finish' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Selector.modelName;
    }

    private _application: Applications;

    private _selectionMethod: SelectionMethods;

    private _minimumFreeArea: number;

    private _width: number;

    private _height: number;

    private _isDamperIncluded: boolean;

    private _model: string;

    private _overrideLimits: boolean;

    private _pressureDrop: number;

    private _velocity: number;

    private _volume: number;

    private _waterPenetrationSafetyFactor: number;

    private _widthIncrement: number;

    private _heightIncrement: number;

    private _capacity: Capacities;

    private _finish: FinishTypes;

    public get application(): Applications {
        // Method Implementation: simple
        return this._application;
    }

    public get selectionMethod(): SelectionMethods {
        // Method Implementation: simple
        return this._selectionMethod;
    }

    public get minimumFreeArea(): number {
        // Method Implementation: simple
        return this._minimumFreeArea;
    }

    public get width(): number {
        // Method Implementation: simple
        return this._width;
    }

    public get height(): number {
        // Method Implementation: simple
        return this._height;
    }

    public get isDamperIncluded(): boolean {
        // Method Implementation: simple
        return this._isDamperIncluded;
    }

    public get model(): string {
        // Method Implementation: simple
        return this._model;
    }

    public get overrideLimits(): boolean {
        // Method Implementation: simple
        return this._overrideLimits;
    }

    public get pressureDrop(): number {
        // Method Implementation: simple
        return this._pressureDrop;
    }

    public get velocity(): number {
        // Method Implementation: simple
        return this._velocity;
    }

    public get volume(): number {
        // Method Implementation: simple
        return this._volume;
    }

    public get waterPenetrationSafetyFactor(): number {
        // Method Implementation: simple
        return this._waterPenetrationSafetyFactor;
    }

    public get widthIncrement(): number {
        // Method Implementation: simple
        return this._widthIncrement;
    }

    public get heightIncrement(): number {
        // Method Implementation: simple
        return this._heightIncrement;
    }

    public get capacity(): Capacities {
        // Method Implementation: simple
        return this._capacity;
    }

    public get finish(): FinishTypes {
        // Method Implementation: simple
        return this._finish;
    }

    public set application(value: Applications) {
        // Method Implementation: simple
        this._application = value;
        this._isApplicationDefined = true;
    }

    public set selectionMethod(value: SelectionMethods) {
        // Method Implementation: simple
        this._selectionMethod = value;
        this._isSelectionMethodDefined = true;
    }

    public set minimumFreeArea(value: number) {
        // Method Implementation: simple
        this._minimumFreeArea = value;
        this._isMinimumFreeAreaDefined = true;
    }

    public set width(value: number) {
        // Method Implementation: simple
        this._width = value;
        this._isWidthDefined = true;
    }

    public set height(value: number) {
        // Method Implementation: simple
        this._height = value;
        this._isHeightDefined = true;
    }

    public set isDamperIncluded(value: boolean) {
        // Method Implementation: simple
        this._isDamperIncluded = value;
        this._isIsDamperIncludedDefined = true;
    }

    public set model(value: string) {
        // Method Implementation: simple
        this._model = value;
        this._isModelDefined = true;
    }

    public set overrideLimits(value: boolean) {
        // Method Implementation: simple
        this._overrideLimits = value;
        this._isOverrideLimitsDefined = true;
    }

    public set pressureDrop(value: number) {
        // Method Implementation: simple
        this._pressureDrop = value;
        this._isPressureDropDefined = true;
    }

    public set velocity(value: number) {
        // Method Implementation: simple
        this._velocity = value;
        this._isVelocityDefined = true;
    }

    public set volume(value: number) {
        // Method Implementation: simple
        this._volume = value;
        this._isVolumeDefined = true;
    }

    public set waterPenetrationSafetyFactor(value: number) {
        // Method Implementation: simple
        this._waterPenetrationSafetyFactor = value;
        this._isWaterPenetrationSafetyFactorDefined = true;
    }

    public set widthIncrement(value: number) {
        // Method Implementation: simple
        this._widthIncrement = value;
        this._isWidthIncrementDefined = true;
    }

    public set heightIncrement(value: number) {
        // Method Implementation: simple
        this._heightIncrement = value;
        this._isHeightIncrementDefined = true;
    }

    public set capacity(value: Capacities) {
        // Method Implementation: simple
        this._capacity = value;
        this._isCapacityDefined = true;
    }

    public set finish(value: FinishTypes) {
        // Method Implementation: simple
        this._finish = value;
        this._isFinishDefined = true;
    }

    private _isApplicationDefined: boolean;

    private _isSelectionMethodDefined: boolean;

    private _isMinimumFreeAreaDefined: boolean;

    private _isWidthDefined: boolean;

    private _isHeightDefined: boolean;

    private _isIsDamperIncludedDefined: boolean;

    private _isModelDefined: boolean;

    private _isOverrideLimitsDefined: boolean;

    private _isPressureDropDefined: boolean;

    private _isVelocityDefined: boolean;

    private _isVolumeDefined: boolean;

    private _isWaterPenetrationSafetyFactorDefined: boolean;

    private _isWidthIncrementDefined: boolean;

    private _isHeightIncrementDefined: boolean;

    private _isCapacityDefined: boolean;

    private _isFinishDefined: boolean;

    public get isApplicationDefined(): boolean {
        // Method Implementation: simple
        return this._isApplicationDefined;
    }

    public get isSelectionMethodDefined(): boolean {
        // Method Implementation: simple
        return this._isSelectionMethodDefined;
    }

    public get isMinimumFreeAreaDefined(): boolean {
        // Method Implementation: simple
        return this._isMinimumFreeAreaDefined;
    }

    public get isWidthDefined(): boolean {
        // Method Implementation: simple
        return this._isWidthDefined;
    }

    public get isHeightDefined(): boolean {
        // Method Implementation: simple
        return this._isHeightDefined;
    }

    public get isIsDamperIncludedDefined(): boolean {
        // Method Implementation: simple
        return this._isIsDamperIncludedDefined;
    }

    public get isModelDefined(): boolean {
        // Method Implementation: simple
        return this._isModelDefined;
    }

    public get isOverrideLimitsDefined(): boolean {
        // Method Implementation: simple
        return this._isOverrideLimitsDefined;
    }

    public get isPressureDropDefined(): boolean {
        // Method Implementation: simple
        return this._isPressureDropDefined;
    }

    public get isVelocityDefined(): boolean {
        // Method Implementation: simple
        return this._isVelocityDefined;
    }

    public get isVolumeDefined(): boolean {
        // Method Implementation: simple
        return this._isVolumeDefined;
    }

    public get isWaterPenetrationSafetyFactorDefined(): boolean {
        // Method Implementation: simple
        return this._isWaterPenetrationSafetyFactorDefined;
    }

    public get isWidthIncrementDefined(): boolean {
        // Method Implementation: simple
        return this._isWidthIncrementDefined;
    }

    public get isHeightIncrementDefined(): boolean {
        // Method Implementation: simple
        return this._isHeightIncrementDefined;
    }

    public get isCapacityDefined(): boolean {
        // Method Implementation: simple
        return this._isCapacityDefined;
    }

    public get isFinishDefined(): boolean {
        // Method Implementation: simple
        return this._isFinishDefined;
    }

    public constructor();
    public constructor(source: ISelector);
    public constructor(source: Partial<Pick<Selector, 'application' | 'selectionMethod' | 'minimumFreeArea' | 'width' | 'height' | 'isDamperIncluded' | 'model' | 'overrideLimits' | 'pressureDrop' | 'velocity' | 'volume' | 'waterPenetrationSafetyFactor' | 'widthIncrement' | 'heightIncrement' | 'capacity' | 'finish'>>);
    public constructor(source?: ISelector | Partial<Pick<Selector, 'application' | 'selectionMethod' | 'minimumFreeArea' | 'width' | 'height' | 'isDamperIncluded' | 'model' | 'overrideLimits' | 'pressureDrop' | 'velocity' | 'volume' | 'waterPenetrationSafetyFactor' | 'widthIncrement' | 'heightIncrement' | 'capacity' | 'finish'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSelectorModel(source)) {
                if (source.isApplicationDefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = Applications.Intake;
                    this._isApplicationDefined = false;
                }
                
                if (source.isSelectionMethodDefined) {
                    this._selectionMethod = source.selectionMethod;
                    this._isSelectionMethodDefined = true;
                }
                else {
                    this._selectionMethod = SelectionMethods.VolumePressure;
                    this._isSelectionMethodDefined = false;
                }
                
                if (source.isMinimumFreeAreaDefined) {
                    this._minimumFreeArea = source.minimumFreeArea;
                    this._isMinimumFreeAreaDefined = true;
                }
                else {
                    this._minimumFreeArea = 0;
                    this._isMinimumFreeAreaDefined = false;
                }
                
                if (source.isWidthDefined) {
                    this._width = source.width;
                    this._isWidthDefined = true;
                }
                else {
                    this._width = 0;
                    this._isWidthDefined = false;
                }
                
                if (source.isHeightDefined) {
                    this._height = source.height;
                    this._isHeightDefined = true;
                }
                else {
                    this._height = 0;
                    this._isHeightDefined = false;
                }
                
                if (source.isIsDamperIncludedDefined) {
                    this._isDamperIncluded = source.isDamperIncluded;
                    this._isIsDamperIncludedDefined = true;
                }
                else {
                    this._isDamperIncluded = false;
                    this._isIsDamperIncludedDefined = false;
                }
                
                if (source.isModelDefined) {
                    this._model = source.model;
                    this._isModelDefined = true;
                }
                else {
                    this._model = '';
                    this._isModelDefined = false;
                }
                
                if (source.isOverrideLimitsDefined) {
                    this._overrideLimits = source.overrideLimits;
                    this._isOverrideLimitsDefined = true;
                }
                else {
                    this._overrideLimits = false;
                    this._isOverrideLimitsDefined = false;
                }
                
                if (source.isPressureDropDefined) {
                    this._pressureDrop = source.pressureDrop;
                    this._isPressureDropDefined = true;
                }
                else {
                    this._pressureDrop = 0;
                    this._isPressureDropDefined = false;
                }
                
                if (source.isVelocityDefined) {
                    this._velocity = source.velocity;
                    this._isVelocityDefined = true;
                }
                else {
                    this._velocity = 0;
                    this._isVelocityDefined = false;
                }
                
                if (source.isVolumeDefined) {
                    this._volume = source.volume;
                    this._isVolumeDefined = true;
                }
                else {
                    this._volume = 0;
                    this._isVolumeDefined = false;
                }
                
                if (source.isWaterPenetrationSafetyFactorDefined) {
                    this._waterPenetrationSafetyFactor = source.waterPenetrationSafetyFactor;
                    this._isWaterPenetrationSafetyFactorDefined = true;
                }
                else {
                    this._waterPenetrationSafetyFactor = 0;
                    this._isWaterPenetrationSafetyFactorDefined = false;
                }
                
                if (source.isWidthIncrementDefined) {
                    this._widthIncrement = source.widthIncrement;
                    this._isWidthIncrementDefined = true;
                }
                else {
                    this._widthIncrement = 0;
                    this._isWidthIncrementDefined = false;
                }
                
                if (source.isHeightIncrementDefined) {
                    this._heightIncrement = source.heightIncrement;
                    this._isHeightIncrementDefined = true;
                }
                else {
                    this._heightIncrement = 0;
                    this._isHeightIncrementDefined = false;
                }
                
                if (source.isCapacityDefined) {
                    this._capacity = source.capacity;
                    this._isCapacityDefined = true;
                }
                else {
                    this._capacity = Capacities.NoPreference;
                    this._isCapacityDefined = false;
                }
                
                if (source.isFinishDefined) {
                    this._finish = source.finish;
                    this._isFinishDefined = true;
                }
                else {
                    this._finish = FinishTypes.None;
                    this._isFinishDefined = false;
                }
            }
            else {
                if (source.application !== undefined) {
                    this._application = source.application;
                    this._isApplicationDefined = true;
                }
                else {
                    this._application = Applications.Intake;
                    this._isApplicationDefined = false;
                }
                
                if (source.selectionMethod !== undefined) {
                    this._selectionMethod = source.selectionMethod;
                    this._isSelectionMethodDefined = true;
                }
                else {
                    this._selectionMethod = SelectionMethods.VolumePressure;
                    this._isSelectionMethodDefined = false;
                }
                
                if (source.minimumFreeArea !== undefined) {
                    this._minimumFreeArea = source.minimumFreeArea;
                    this._isMinimumFreeAreaDefined = true;
                }
                else {
                    this._minimumFreeArea = 0;
                    this._isMinimumFreeAreaDefined = false;
                }
                
                if (source.width !== undefined) {
                    this._width = source.width;
                    this._isWidthDefined = true;
                }
                else {
                    this._width = 0;
                    this._isWidthDefined = false;
                }
                
                if (source.height !== undefined) {
                    this._height = source.height;
                    this._isHeightDefined = true;
                }
                else {
                    this._height = 0;
                    this._isHeightDefined = false;
                }
                
                if (source.isDamperIncluded !== undefined) {
                    this._isDamperIncluded = source.isDamperIncluded;
                    this._isIsDamperIncludedDefined = true;
                }
                else {
                    this._isDamperIncluded = false;
                    this._isIsDamperIncludedDefined = false;
                }
                
                if (source.model !== undefined) {
                    this._model = source.model;
                    this._isModelDefined = true;
                }
                else {
                    this._model = '';
                    this._isModelDefined = false;
                }
                
                if (source.overrideLimits !== undefined) {
                    this._overrideLimits = source.overrideLimits;
                    this._isOverrideLimitsDefined = true;
                }
                else {
                    this._overrideLimits = false;
                    this._isOverrideLimitsDefined = false;
                }
                
                if (source.pressureDrop !== undefined) {
                    this._pressureDrop = source.pressureDrop;
                    this._isPressureDropDefined = true;
                }
                else {
                    this._pressureDrop = 0;
                    this._isPressureDropDefined = false;
                }
                
                if (source.velocity !== undefined) {
                    this._velocity = source.velocity;
                    this._isVelocityDefined = true;
                }
                else {
                    this._velocity = 0;
                    this._isVelocityDefined = false;
                }
                
                if (source.volume !== undefined) {
                    this._volume = source.volume;
                    this._isVolumeDefined = true;
                }
                else {
                    this._volume = 0;
                    this._isVolumeDefined = false;
                }
                
                if (source.waterPenetrationSafetyFactor !== undefined) {
                    this._waterPenetrationSafetyFactor = source.waterPenetrationSafetyFactor;
                    this._isWaterPenetrationSafetyFactorDefined = true;
                }
                else {
                    this._waterPenetrationSafetyFactor = 0;
                    this._isWaterPenetrationSafetyFactorDefined = false;
                }
                
                if (source.widthIncrement !== undefined) {
                    this._widthIncrement = source.widthIncrement;
                    this._isWidthIncrementDefined = true;
                }
                else {
                    this._widthIncrement = 0;
                    this._isWidthIncrementDefined = false;
                }
                
                if (source.heightIncrement !== undefined) {
                    this._heightIncrement = source.heightIncrement;
                    this._isHeightIncrementDefined = true;
                }
                else {
                    this._heightIncrement = 0;
                    this._isHeightIncrementDefined = false;
                }
                
                if (source.capacity !== undefined) {
                    this._capacity = source.capacity;
                    this._isCapacityDefined = true;
                }
                else {
                    this._capacity = Capacities.NoPreference;
                    this._isCapacityDefined = false;
                }
                
                if (source.finish !== undefined) {
                    this._finish = source.finish;
                    this._isFinishDefined = true;
                }
                else {
                    this._finish = FinishTypes.None;
                    this._isFinishDefined = false;
                }
            }
        }
        else {
            this._application = Applications.Intake;
            this._isApplicationDefined = false;
        
            this._selectionMethod = SelectionMethods.VolumePressure;
            this._isSelectionMethodDefined = false;
        
            this._minimumFreeArea = 0;
            this._isMinimumFreeAreaDefined = false;
        
            this._width = 0;
            this._isWidthDefined = false;
        
            this._height = 0;
            this._isHeightDefined = false;
        
            this._isDamperIncluded = false;
            this._isIsDamperIncludedDefined = false;
        
            this._model = '';
            this._isModelDefined = false;
        
            this._overrideLimits = false;
            this._isOverrideLimitsDefined = false;
        
            this._pressureDrop = 0;
            this._isPressureDropDefined = false;
        
            this._velocity = 0;
            this._isVelocityDefined = false;
        
            this._volume = 0;
            this._isVolumeDefined = false;
        
            this._waterPenetrationSafetyFactor = 0;
            this._isWaterPenetrationSafetyFactorDefined = false;
        
            this._widthIncrement = 0;
            this._isWidthIncrementDefined = false;
        
            this._heightIncrement = 0;
            this._isHeightIncrementDefined = false;
        
            this._capacity = Capacities.NoPreference;
            this._isCapacityDefined = false;
        
            this._finish = FinishTypes.None;
            this._isFinishDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Selector.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isApplicationDefined && this.application !== undefined)
            definedProperties.push('application');
        if (this.isSelectionMethodDefined && this.selectionMethod !== undefined)
            definedProperties.push('selectionMethod');
        if (this.isMinimumFreeAreaDefined && this.minimumFreeArea !== undefined)
            definedProperties.push('minimumFreeArea');
        if (this.isWidthDefined && this.width !== undefined)
            definedProperties.push('width');
        if (this.isHeightDefined && this.height !== undefined)
            definedProperties.push('height');
        if (this.isIsDamperIncludedDefined && this.isDamperIncluded !== undefined)
            definedProperties.push('isDamperIncluded');
        if (this.isModelDefined && this.model !== undefined)
            definedProperties.push('model');
        if (this.isOverrideLimitsDefined && this.overrideLimits !== undefined)
            definedProperties.push('overrideLimits');
        if (this.isPressureDropDefined && this.pressureDrop !== undefined)
            definedProperties.push('pressureDrop');
        if (this.isVelocityDefined && this.velocity !== undefined)
            definedProperties.push('velocity');
        if (this.isVolumeDefined && this.volume !== undefined)
            definedProperties.push('volume');
        if (this.isWaterPenetrationSafetyFactorDefined && this.waterPenetrationSafetyFactor !== undefined)
            definedProperties.push('waterPenetrationSafetyFactor');
        if (this.isWidthIncrementDefined && this.widthIncrement !== undefined)
            definedProperties.push('widthIncrement');
        if (this.isHeightIncrementDefined && this.heightIncrement !== undefined)
            definedProperties.push('heightIncrement');
        if (this.isCapacityDefined && this.capacity !== undefined)
            definedProperties.push('capacity');
        if (this.isFinishDefined && this.finish !== undefined)
            definedProperties.push('finish');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'application':
                return this.application;
            case 'selectionMethod':
                return this.selectionMethod;
            case 'minimumFreeArea':
                return this.minimumFreeArea;
            case 'width':
                return this.width;
            case 'height':
                return this.height;
            case 'isDamperIncluded':
                return this.isDamperIncluded;
            case 'model':
                return this.model;
            case 'overrideLimits':
                return this.overrideLimits;
            case 'pressureDrop':
                return this.pressureDrop;
            case 'velocity':
                return this.velocity;
            case 'volume':
                return this.volume;
            case 'waterPenetrationSafetyFactor':
                return this.waterPenetrationSafetyFactor;
            case 'widthIncrement':
                return this.widthIncrement;
            case 'heightIncrement':
                return this.heightIncrement;
            case 'capacity':
                return this.capacity;
            case 'finish':
                return this.finish;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isApplicationDefined && this.application !== undefined) {
            if (this.application !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.application);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('application', 'null'));
            }
        }
        
        if (this.isSelectionMethodDefined && this.selectionMethod !== undefined) {
            if (this.selectionMethod !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('selectionMethod', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.selectionMethod);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('selectionMethod', 'null'));
            }
        }
        
        if (this.isMinimumFreeAreaDefined && this.minimumFreeArea !== undefined) {
            if (this.minimumFreeArea !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('minimumFreeArea', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.minimumFreeArea);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('minimumFreeArea', 'null'));
            }
        }
        
        if (this.isWidthDefined && this.width !== undefined) {
            if (this.width !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('width', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.width);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('width', 'null'));
            }
        }
        
        if (this.isHeightDefined && this.height !== undefined) {
            if (this.height !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('height', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.height);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('height', 'null'));
            }
        }
        
        if (this.isIsDamperIncludedDefined && this.isDamperIncluded !== undefined) {
            if (this.isDamperIncluded !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('isDamperIncluded', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.isDamperIncluded);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('isDamperIncluded', 'null'));
            }
        }
        
        if (this.isModelDefined && this.model !== undefined) {
            if (this.model !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('model', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.model);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('model', 'null'));
            }
        }
        
        if (this.isOverrideLimitsDefined && this.overrideLimits !== undefined) {
            if (this.overrideLimits !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('overrideLimits', 'boolean'));
        
                // Implementation: axon/write/boolean
                writer.writeBooleanValue(this.overrideLimits);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('overrideLimits', 'null'));
            }
        }
        
        if (this.isPressureDropDefined && this.pressureDrop !== undefined) {
            if (this.pressureDrop !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pressureDrop', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.pressureDrop);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pressureDrop', 'null'));
            }
        }
        
        if (this.isVelocityDefined && this.velocity !== undefined) {
            if (this.velocity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('velocity', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.velocity);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('velocity', 'null'));
            }
        }
        
        if (this.isVolumeDefined && this.volume !== undefined) {
            if (this.volume !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('volume', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.volume);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('volume', 'null'));
            }
        }
        
        if (this.isWaterPenetrationSafetyFactorDefined && this.waterPenetrationSafetyFactor !== undefined) {
            if (this.waterPenetrationSafetyFactor !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('waterPenetrationSafetyFactor', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.waterPenetrationSafetyFactor);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('waterPenetrationSafetyFactor', 'null'));
            }
        }
        
        if (this.isWidthIncrementDefined && this.widthIncrement !== undefined) {
            if (this.widthIncrement !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('widthIncrement', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.widthIncrement);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('widthIncrement', 'null'));
            }
        }
        
        if (this.isHeightIncrementDefined && this.heightIncrement !== undefined) {
            if (this.heightIncrement !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('heightIncrement', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.heightIncrement);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('heightIncrement', 'null'));
            }
        }
        
        if (this.isCapacityDefined && this.capacity !== undefined) {
            if (this.capacity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('capacity', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.capacity);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('capacity', 'null'));
            }
        }
        
        if (this.isFinishDefined && this.finish !== undefined) {
            if (this.finish !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('finish', 'enum'));
        
                // Implementation: axon/write/enum
                writer.writeEnumValue(this.finish);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('finish', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'application':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.application = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'application' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'application'`);
                }
        
                return true;
            case 'selectionMethod':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.selectionMethod = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'selectionMethod' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'selectionMethod'`);
                }
        
                return true;
            case 'minimumFreeArea':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.minimumFreeArea = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.minimumFreeArea = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'minimumFreeArea' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'minimumFreeArea'`);
                }
        
                return true;
            case 'width':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.width = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.width = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'width' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'width'`);
                }
        
                return true;
            case 'height':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.height = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.height = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'height' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'height'`);
                }
        
                return true;
            case 'isDamperIncluded':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.isDamperIncluded = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'isDamperIncluded' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'isDamperIncluded'`);
                }
        
                return true;
            case 'model':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.model = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'model' cannot be null`);
                    this.model = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'model'`);
                }
        
                return true;
            case 'overrideLimits':
                // Implementation: axon/readProperty/boolean
                if (propertyHeader.type === 'boolean') {
                    this.overrideLimits = reader.readBooleanValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'overrideLimits' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'overrideLimits'`);
                }
        
                return true;
            case 'pressureDrop':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.pressureDrop = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.pressureDrop = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'pressureDrop' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'pressureDrop'`);
                }
        
                return true;
            case 'velocity':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.velocity = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.velocity = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'velocity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'velocity'`);
                }
        
                return true;
            case 'volume':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.volume = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.volume = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'volume' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'volume'`);
                }
        
                return true;
            case 'waterPenetrationSafetyFactor':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.waterPenetrationSafetyFactor = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.waterPenetrationSafetyFactor = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'waterPenetrationSafetyFactor' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'waterPenetrationSafetyFactor'`);
                }
        
                return true;
            case 'widthIncrement':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.widthIncrement = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.widthIncrement = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'widthIncrement' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'widthIncrement'`);
                }
        
                return true;
            case 'heightIncrement':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.heightIncrement = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.heightIncrement = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'heightIncrement' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'heightIncrement'`);
                }
        
                return true;
            case 'capacity':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.capacity = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'capacity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'capacity'`);
                }
        
                return true;
            case 'finish':
                // Implementation: axon/readProperty/enum
                if (propertyHeader.type === 'enum') {
                    this.finish = reader.readEnumValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Property 'finish' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'finish'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // application
        if (this.isApplicationDefined && this.application !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.application);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // selectionMethod
        if (this.isSelectionMethodDefined && this.selectionMethod !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.selectionMethod);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // minimumFreeArea
        if (this.isMinimumFreeAreaDefined && this.minimumFreeArea !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.minimumFreeArea);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // width
        if (this.isWidthDefined && this.width !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.width);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // height
        if (this.isHeightDefined && this.height !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.height);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // isDamperIncluded
        if (this.isIsDamperIncludedDefined && this.isDamperIncluded !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.isDamperIncluded);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // model
        if (this.isModelDefined && this.model !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.model === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.model);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // overrideLimits
        if (this.isOverrideLimitsDefined && this.overrideLimits !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/boolean
            writer.writeBooleanValue(this.overrideLimits);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // pressureDrop
        if (this.isPressureDropDefined && this.pressureDrop !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.pressureDrop);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // velocity
        if (this.isVelocityDefined && this.velocity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.velocity);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // volume
        if (this.isVolumeDefined && this.volume !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.volume);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // waterPenetrationSafetyFactor
        if (this.isWaterPenetrationSafetyFactorDefined && this.waterPenetrationSafetyFactor !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.waterPenetrationSafetyFactor);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // widthIncrement
        if (this.isWidthIncrementDefined && this.widthIncrement !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.widthIncrement);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // heightIncrement
        if (this.isHeightIncrementDefined && this.heightIncrement !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.heightIncrement);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // capacity
        if (this.isCapacityDefined && this.capacity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.capacity);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // finish
        if (this.isFinishDefined && this.finish !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/enum
            writer.writeEnumValue(this.finish);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // application
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.application = reader.readEnumValue();
        }
        
        // selectionMethod
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.selectionMethod = reader.readEnumValue();
        }
        
        // minimumFreeArea
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.minimumFreeArea = reader.readDoubleValue();
        }
        
        // width
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.width = reader.readDoubleValue();
        }
        
        // height
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.height = reader.readDoubleValue();
        }
        
        // isDamperIncluded
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.isDamperIncluded = reader.readBooleanValue();
        }
        
        // model
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.model = <any>null;
                    break;
                case 1:
                    this.model = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // overrideLimits
        if (reader.readBooleanValue())
        {
            // Implementation: boolean
            this.overrideLimits = reader.readBooleanValue();
        }
        
        // pressureDrop
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.pressureDrop = reader.readDoubleValue();
        }
        
        // velocity
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.velocity = reader.readDoubleValue();
        }
        
        // volume
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.volume = reader.readDoubleValue();
        }
        
        // waterPenetrationSafetyFactor
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.waterPenetrationSafetyFactor = reader.readDoubleValue();
        }
        
        // widthIncrement
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.widthIncrement = reader.readDoubleValue();
        }
        
        // heightIncrement
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.heightIncrement = reader.readDoubleValue();
        }
        
        // capacity
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.capacity = reader.readEnumValue();
        }
        
        // finish
        if (reader.readBooleanValue())
        {
            // Implementation: enum
            this.finish = reader.readEnumValue();
        }
    }

    public static parse(hash: unknown): Selector {
        // Method Implementation: simple
        const schema = SelectorZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SelectorZodSchemaShape): Selector {
        // Method Implementation: parseZodSchema
        return new Selector({
            application: (() => {
            if (!schema.application)
                return;
        
            switch (schema.application) {
                case 'Intake':
                    return Applications.Intake;
                case 'Exhaust':
                    return Applications.Exhaust;
                default:
                    throw new Error(`Enum type "schema.application" not recognized`);
            }
        })(),
            selectionMethod: (() => {
            if (!schema.selectionMethod)
                return;
        
            switch (schema.selectionMethod) {
                case 'VolumePressure':
                    return SelectionMethods.VolumePressure;
                case 'VolumePressureWidth':
                    return SelectionMethods.VolumePressureWidth;
                case 'VolumePressureHeight':
                    return SelectionMethods.VolumePressureHeight;
                case 'VolumeVelocity':
                    return SelectionMethods.VolumeVelocity;
                case 'VolumeVelocityWidth':
                    return SelectionMethods.VolumeVelocityWidth;
                case 'VolumeVelocityHeight':
                    return SelectionMethods.VolumeVelocityHeight;
                case 'VolumeWidthHeight':
                    return SelectionMethods.VolumeWidthHeight;
                case 'PressureWidthHeight':
                    return SelectionMethods.PressureWidthHeight;
                case 'WidthHeight':
                    return SelectionMethods.WidthHeight;
                case 'FreeArea':
                    return SelectionMethods.FreeArea;
                default:
                    throw new Error(`Enum type "schema.selectionMethod" not recognized`);
            }
        })(),
            minimumFreeArea: schema.minimumFreeArea,
            width: schema.width,
            height: schema.height,
            isDamperIncluded: schema.isDamperIncluded,
            model: schema.model,
            overrideLimits: schema.overrideLimits,
            pressureDrop: schema.pressureDrop,
            velocity: schema.velocity,
            volume: schema.volume,
            waterPenetrationSafetyFactor: schema.waterPenetrationSafetyFactor,
            widthIncrement: schema.widthIncrement,
            heightIncrement: schema.heightIncrement,
            capacity: (() => {
            if (!schema.capacity)
                return;
        
            switch (schema.capacity) {
                case 'NoPreference':
                    return Capacities.NoPreference;
                case 'ClassA29_3':
                    return Capacities.ClassA29_3;
                case 'ClassA50_8':
                    return Capacities.ClassA50_8;
                case 'BPWP':
                    return Capacities.BPWP;
                default:
                    throw new Error(`Enum type "schema.capacity" not recognized`);
            }
        })(),
            finish: (() => {
            if (!schema.finish)
                return;
        
            switch (schema.finish) {
                case 'None':
                    return FinishTypes.None;
                case 'Kynar_70':
                    return FinishTypes.Kynar_70;
                case 'Kynar3Coat':
                    return FinishTypes.Kynar3Coat;
                case 'Kynar4Coat':
                    return FinishTypes.Kynar4Coat;
                case 'Acroflur':
                    return FinishTypes.Acroflur;
                case 'Anodize':
                    return FinishTypes.Anodize;
                case 'BakedEnamel':
                    return FinishTypes.BakedEnamel;
                case 'AcrylicEnamel':
                    return FinishTypes.AcrylicEnamel;
                case 'Primer':
                    return FinishTypes.Primer;
                case 'Mica':
                    return FinishTypes.Mica;
                case 'Pearlecent':
                    return FinishTypes.Pearlecent;
                case 'Epoxy':
                    return FinishTypes.Epoxy;
                case 'HiProPolyester':
                    return FinishTypes.HiProPolyester;
                case 'WoodGrain':
                    return FinishTypes.WoodGrain;
                default:
                    throw new Error(`Enum type "schema.finish" not recognized`);
            }
        })()
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            application: this.isApplicationDefined ? (Applications[this.application]) : undefined,
            selectionMethod: this.isSelectionMethodDefined ? (SelectionMethods[this.selectionMethod]) : undefined,
            minimumFreeArea: this.isMinimumFreeAreaDefined ? (this.minimumFreeArea) : undefined,
            width: this.isWidthDefined ? (this.width) : undefined,
            height: this.isHeightDefined ? (this.height) : undefined,
            isDamperIncluded: this.isIsDamperIncludedDefined ? (this.isDamperIncluded) : undefined,
            model: this.isModelDefined ? ((this.model === null ? undefined : this.model)) : undefined,
            overrideLimits: this.isOverrideLimitsDefined ? (this.overrideLimits) : undefined,
            pressureDrop: this.isPressureDropDefined ? (this.pressureDrop) : undefined,
            velocity: this.isVelocityDefined ? (this.velocity) : undefined,
            volume: this.isVolumeDefined ? (this.volume) : undefined,
            waterPenetrationSafetyFactor: this.isWaterPenetrationSafetyFactorDefined ? (this.waterPenetrationSafetyFactor) : undefined,
            widthIncrement: this.isWidthIncrementDefined ? (this.widthIncrement) : undefined,
            heightIncrement: this.isHeightIncrementDefined ? (this.heightIncrement) : undefined,
            capacity: this.isCapacityDefined ? (Capacities[this.capacity]) : undefined,
            finish: this.isFinishDefined ? (FinishTypes[this.finish]) : undefined,
        };
    }

    public clone(): Selector {
        // Method Implementation: clone
        return new Selector({
            application: this.isApplicationDefined ? this.application : undefined,
            selectionMethod: this.isSelectionMethodDefined ? this.selectionMethod : undefined,
            minimumFreeArea: this.isMinimumFreeAreaDefined ? this.minimumFreeArea : undefined,
            width: this.isWidthDefined ? this.width : undefined,
            height: this.isHeightDefined ? this.height : undefined,
            isDamperIncluded: this.isIsDamperIncludedDefined ? this.isDamperIncluded : undefined,
            model: this.isModelDefined ? this.model : undefined,
            overrideLimits: this.isOverrideLimitsDefined ? this.overrideLimits : undefined,
            pressureDrop: this.isPressureDropDefined ? this.pressureDrop : undefined,
            velocity: this.isVelocityDefined ? this.velocity : undefined,
            volume: this.isVolumeDefined ? this.volume : undefined,
            waterPenetrationSafetyFactor: this.isWaterPenetrationSafetyFactorDefined ? this.waterPenetrationSafetyFactor : undefined,
            widthIncrement: this.isWidthIncrementDefined ? this.widthIncrement : undefined,
            heightIncrement: this.isHeightIncrementDefined ? this.heightIncrement : undefined,
            capacity: this.isCapacityDefined ? this.capacity : undefined,
            finish: this.isFinishDefined ? this.finish : undefined
        });
    }
}

export class Selection implements ISelection {
    public static readonly modelName: string = 'Selection';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'sizes' ];

    public get modelName(): string {
        // Method Implementation: simple
        return Selection.modelName;
    }

    private _sizes: ValidSize[];

    public get sizes(): ValidSize[] {
        // Method Implementation: simple
        return this._sizes;
    }

    public set sizes(value: ValidSize[]) {
        // Method Implementation: simple
        this._sizes = value;
        this._isSizesDefined = true;
    }

    private _isSizesDefined: boolean;

    public get isSizesDefined(): boolean {
        // Method Implementation: simple
        return this._isSizesDefined;
    }

    public constructor();
    public constructor(source: ISelection);
    public constructor(source: Partial<Pick<Selection, 'sizes'>>);
    public constructor(source?: ISelection | Partial<Pick<Selection, 'sizes'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isSelectionModel(source)) {
                if (source.isSizesDefined) {
                    this._sizes = source.sizes;
                    this._isSizesDefined = true;
                }
                else {
                    this._sizes = [];
                    this._isSizesDefined = true;
                }
            }
            else {
                if (source.sizes !== undefined) {
                    this._sizes = source.sizes;
                    this._isSizesDefined = true;
                }
                else {
                    this._sizes = [];
                    this._isSizesDefined = true;
                }
            }
        }
        else {
            this._sizes = [];
            this._isSizesDefined = true;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return Selection.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isSizesDefined && this.sizes !== undefined)
            definedProperties.push('sizes');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'sizes':
                return this.sizes;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isSizesDefined && this.sizes !== undefined) {
            if (this.sizes !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizes', 'array'));
        
                // Implementation: axon/write/array
                writer.writeArrayHeader(new Axon.ArrayHeader(this.sizes.length));
                for (let i = 0; i < this.sizes.length; i++) {
                    const item = this.sizes[i];
                
                    if (item !== null) {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('model'));
                
                        // Implementation: axon/write/model
                        writer.writeModelHeader(new Axon.ModelHeader(item.getModelName(), item.getDefinedPropertyNames().length));
                        item.write(writer);
                    }
                    else {
                        writer.writeArrayItemHeader(new Axon.ArrayItemHeader('null'));
                    }
                }
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('sizes', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'sizes':
                // Implementation: axon/readProperty/array
                if (propertyHeader.type === 'array') {
                    const arrayHeader = reader.readArrayHeader();
                
                    this.sizes = [];
                
                    let item: ValidSize = <any>undefined;
                    for (let i = 0; i < arrayHeader.itemCount; i++) {
                        const arrayItemHeader = reader.readArrayItemHeader();
                
                        // Implementation: model
                        if (arrayItemHeader.type === 'model') {
                            const modelHeader = reader.readModelHeader();
                        
                            if (modelHeader.modelName === ValidSize.modelName) {
                                item = new ValidSize();
                                item.read(reader, modelHeader);
                            }
                            else {
                                throw new Error(`Unexpected model type '${modelHeader.modelName}'; expected '${ValidSize.modelName}'`);
                            }
                        }
                        else if (arrayItemHeader.type === 'null') {
                            throw new Error(`Value for property 'sizes' cannot be null`);
                        }
                        else {
                            throw new Error(`Unexpected data type '${arrayItemHeader.type}' for property 'sizes'`);
                        }
                
                        this.sizes.push(item);
                    }
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'sizes' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'sizes'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // sizes
        if (this.isSizesDefined && this.sizes !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/array
            if (this.sizes === null) {
                writer.writeByteValue(0);
            }
            else {
                writer.writeByteValue(1);
                writer.writeIntegerValue(this.sizes.length);
            
                for (const item of this.sizes) {
                    // Item Implementation: model
                    if (item === null) {
                        writer.writeByteValue(0);
                    }
                    else {
                        writer.writeByteValue(1);
                        item.slipstreamWrite(writer);
                    }
                }
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // sizes
        if (reader.readBooleanValue())
        {
            // Implementation: array
            switch (reader.readByteValue())
            {
                case 0:
                    throw new Error('Cannot process null arrays');
                    break;
                case 1:
                    let item: ValidSize;
            
                    this.sizes = [];
                    
                    const length = reader.readIntegerValue();
                    for (let i = 0; i < length; i++) {
                        // Item Implementation: model
                        switch (reader.readByteValue())
                        {
                            case 0:
                                throw new Error('Cannot process null models');
                                break;
                            case 1:
                                item = new ValidSize();
                                item.slipstreamRead(reader);
                                break;
                            default:
                                throw new Error('Invalid null code');
                        }
            
                        this.sizes.push(item);
                    }
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
    }

    public static parse(hash: unknown): Selection {
        // Method Implementation: simple
        const schema = SelectionZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: SelectionZodSchemaShape): Selection {
        // Method Implementation: parseZodSchema
        return new Selection({
            sizes: schema.sizes?.map(i => ValidSize.parseZodSchema(i))
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            sizes: this.isSizesDefined ? (this.sizes?.map(item => item)) : undefined,
        };
    }

    public clone(): Selection {
        // Method Implementation: clone
        return new Selection({
            sizes: this.isSizesDefined ? this.sizes?.map(i => i.clone()) : undefined
        });
    }
}

export class ValidSize implements IValidSize {
    public static readonly modelName: string = 'ValidSize';

    public static readonly propertyNames: ReadonlyArray<string> = [ 'name', 'freeArea', 'pressureDrop', 'velocity', 'volume' ];

    public get modelName(): string {
        // Method Implementation: simple
        return ValidSize.modelName;
    }

    private _name: string;

    private _freeArea: number;

    private _pressureDrop: number;

    private _velocity: number;

    private _volume: number;

    public get name(): string {
        // Method Implementation: simple
        return this._name;
    }

    public get freeArea(): number {
        // Method Implementation: simple
        return this._freeArea;
    }

    public get pressureDrop(): number {
        // Method Implementation: simple
        return this._pressureDrop;
    }

    public get velocity(): number {
        // Method Implementation: simple
        return this._velocity;
    }

    public get volume(): number {
        // Method Implementation: simple
        return this._volume;
    }

    public set name(value: string) {
        // Method Implementation: simple
        this._name = value;
        this._isNameDefined = true;
    }

    public set freeArea(value: number) {
        // Method Implementation: simple
        this._freeArea = value;
        this._isFreeAreaDefined = true;
    }

    public set pressureDrop(value: number) {
        // Method Implementation: simple
        this._pressureDrop = value;
        this._isPressureDropDefined = true;
    }

    public set velocity(value: number) {
        // Method Implementation: simple
        this._velocity = value;
        this._isVelocityDefined = true;
    }

    public set volume(value: number) {
        // Method Implementation: simple
        this._volume = value;
        this._isVolumeDefined = true;
    }

    private _isNameDefined: boolean;

    private _isFreeAreaDefined: boolean;

    private _isPressureDropDefined: boolean;

    private _isVelocityDefined: boolean;

    private _isVolumeDefined: boolean;

    public get isNameDefined(): boolean {
        // Method Implementation: simple
        return this._isNameDefined;
    }

    public get isFreeAreaDefined(): boolean {
        // Method Implementation: simple
        return this._isFreeAreaDefined;
    }

    public get isPressureDropDefined(): boolean {
        // Method Implementation: simple
        return this._isPressureDropDefined;
    }

    public get isVelocityDefined(): boolean {
        // Method Implementation: simple
        return this._isVelocityDefined;
    }

    public get isVolumeDefined(): boolean {
        // Method Implementation: simple
        return this._isVolumeDefined;
    }

    public constructor();
    public constructor(source: IValidSize);
    public constructor(source: Partial<Pick<ValidSize, 'name' | 'freeArea' | 'pressureDrop' | 'velocity' | 'volume'>>);
    public constructor(source?: IValidSize | Partial<Pick<ValidSize, 'name' | 'freeArea' | 'pressureDrop' | 'velocity' | 'volume'>>) {
        // Method Implementation: modelConstructor
        if (!!source) {
            if (isValidSizeModel(source)) {
                if (source.isNameDefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.isFreeAreaDefined) {
                    this._freeArea = source.freeArea;
                    this._isFreeAreaDefined = true;
                }
                else {
                    this._freeArea = 0;
                    this._isFreeAreaDefined = false;
                }
                
                if (source.isPressureDropDefined) {
                    this._pressureDrop = source.pressureDrop;
                    this._isPressureDropDefined = true;
                }
                else {
                    this._pressureDrop = 0;
                    this._isPressureDropDefined = false;
                }
                
                if (source.isVelocityDefined) {
                    this._velocity = source.velocity;
                    this._isVelocityDefined = true;
                }
                else {
                    this._velocity = 0;
                    this._isVelocityDefined = false;
                }
                
                if (source.isVolumeDefined) {
                    this._volume = source.volume;
                    this._isVolumeDefined = true;
                }
                else {
                    this._volume = 0;
                    this._isVolumeDefined = false;
                }
            }
            else {
                if (source.name !== undefined) {
                    this._name = source.name;
                    this._isNameDefined = true;
                }
                else {
                    this._name = '';
                    this._isNameDefined = false;
                }
                
                if (source.freeArea !== undefined) {
                    this._freeArea = source.freeArea;
                    this._isFreeAreaDefined = true;
                }
                else {
                    this._freeArea = 0;
                    this._isFreeAreaDefined = false;
                }
                
                if (source.pressureDrop !== undefined) {
                    this._pressureDrop = source.pressureDrop;
                    this._isPressureDropDefined = true;
                }
                else {
                    this._pressureDrop = 0;
                    this._isPressureDropDefined = false;
                }
                
                if (source.velocity !== undefined) {
                    this._velocity = source.velocity;
                    this._isVelocityDefined = true;
                }
                else {
                    this._velocity = 0;
                    this._isVelocityDefined = false;
                }
                
                if (source.volume !== undefined) {
                    this._volume = source.volume;
                    this._isVolumeDefined = true;
                }
                else {
                    this._volume = 0;
                    this._isVolumeDefined = false;
                }
            }
        }
        else {
            this._name = '';
            this._isNameDefined = false;
        
            this._freeArea = 0;
            this._isFreeAreaDefined = false;
        
            this._pressureDrop = 0;
            this._isPressureDropDefined = false;
        
            this._velocity = 0;
            this._isVelocityDefined = false;
        
            this._volume = 0;
            this._isVolumeDefined = false;
        }
    }

    public getModelName(): string {
        // Method Implementation: simple
        return this.modelName;
    }

    public getPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: simple
        return ValidSize.propertyNames;
    }

    public getDefinedPropertyNames(): ReadonlyArray<string> {
        // Method Implementation: modelHelpers/getDefinedPropertyNames
        const definedProperties: Array<string> = [];
        
        if (this.isNameDefined && this.name !== undefined)
            definedProperties.push('name');
        if (this.isFreeAreaDefined && this.freeArea !== undefined)
            definedProperties.push('freeArea');
        if (this.isPressureDropDefined && this.pressureDrop !== undefined)
            definedProperties.push('pressureDrop');
        if (this.isVelocityDefined && this.velocity !== undefined)
            definedProperties.push('velocity');
        if (this.isVolumeDefined && this.volume !== undefined)
            definedProperties.push('volume');
        
        return definedProperties;
    }

    public getPropertyValue(propertyName: string): any {
        // Method Implementation: modelHelpers/getPropertyValue
        switch (propertyName) {
            case 'name':
                return this.name;
            case 'freeArea':
                return this.freeArea;
            case 'pressureDrop':
                return this.pressureDrop;
            case 'velocity':
                return this.velocity;
            case 'volume':
                return this.volume;
            default:
                throw new Error(`Property ${propertyName} not recognized`);
        }
    }

    public write(writer: Axon.IProtocolWriter) {
        // Method Implementation: axon/write
        if (this.isNameDefined && this.name !== undefined) {
            if (this.name !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'string'));
        
                // Implementation: axon/write/string
                writer.writeStringValue(this.name);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('name', 'null'));
            }
        }
        
        if (this.isFreeAreaDefined && this.freeArea !== undefined) {
            if (this.freeArea !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('freeArea', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.freeArea);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('freeArea', 'null'));
            }
        }
        
        if (this.isPressureDropDefined && this.pressureDrop !== undefined) {
            if (this.pressureDrop !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pressureDrop', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.pressureDrop);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('pressureDrop', 'null'));
            }
        }
        
        if (this.isVelocityDefined && this.velocity !== undefined) {
            if (this.velocity !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('velocity', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.velocity);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('velocity', 'null'));
            }
        }
        
        if (this.isVolumeDefined && this.volume !== undefined) {
            if (this.volume !== null) {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('volume', 'double'));
        
                // Implementation: axon/write/double
                writer.writeDoubleValue(this.volume);
            }
            else {
                writer.writeModelPropertyHeader(new Axon.ModelPropertyHeader('volume', 'null'));
            }
        }
    }

    public read(reader: Axon.IProtocolReader, modelHeader: Axon.IModelHeader) {
        // Method Implementation: axon/read
        for (let a = 0; a < modelHeader.propertyCount; a++) {
            const propertyHeader = reader.readModelPropertyHeader();
        
            if (!this.readProperty(reader, propertyHeader))
                throw new Error(`Property ${propertyHeader.propertyName} not recognized`);
        }
    }

    public readProperty(reader: Axon.IProtocolReader, propertyHeader: Axon.IModelPropertyHeader) {
        // Method Implementation: axon/readProperty
        switch (propertyHeader.propertyName) {
            case 'name':
                // Implementation: axon/readProperty/string
                if (propertyHeader.type === 'string') {
                    this.name = reader.readStringValue();
                }
                else if (propertyHeader.type === 'null') {
                    // Do Nothing - TODO Review
                    // throw new Error(`Value for property 'name' cannot be null`);
                    this.name = <any>null;
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'name'`);
                }
        
                return true;
            case 'freeArea':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.freeArea = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.freeArea = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'freeArea' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'freeArea'`);
                }
        
                return true;
            case 'pressureDrop':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.pressureDrop = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.pressureDrop = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'pressureDrop' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'pressureDrop'`);
                }
        
                return true;
            case 'velocity':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.velocity = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.velocity = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'velocity' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'velocity'`);
                }
        
                return true;
            case 'volume':
                // Implementation: axon/readProperty/double
                if (propertyHeader.type === 'double') {
                    this.volume = reader.readDoubleValue();
                }
                else if (propertyHeader.type === 'single') {
                    this.volume = reader.readFloatValue();
                }
                else if (propertyHeader.type === 'null') {
                    throw new Error(`Value for property 'volume' cannot be null`);
                }
                else {
                    throw new Error(`Unexpected data type '${propertyHeader.type}' for property 'volume'`);
                }
        
                return true;
            default:
                return false;
        }
    }

    public slipstreamWrite(writer: Axon.IProtocolWriter) {
        // Method Implementation: slipstream/write
        // name
        if (this.isNameDefined && this.name !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/string
            if (this.name === null)
            {
                writer.writeByteValue(0);
            }
            else
            {
                writer.writeByteValue(1);
                writer.writeStringValue(this.name);
            }
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // freeArea
        if (this.isFreeAreaDefined && this.freeArea !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.freeArea);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // pressureDrop
        if (this.isPressureDropDefined && this.pressureDrop !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.pressureDrop);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // velocity
        if (this.isVelocityDefined && this.velocity !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.velocity);
        }
        else {
            writer.writeBooleanValue(false);
        }
        
        // volume
        if (this.isVolumeDefined && this.volume !== undefined) {
            writer.writeBooleanValue(true);
            // Implementation: axon/write/double
            writer.writeDoubleValue(this.volume);
        }
        else {
            writer.writeBooleanValue(false);
        }
    }

    public slipstreamRead(reader: Axon.IProtocolReader) {
        // Method Implementation: slipstream/read
        // name
        if (reader.readBooleanValue())
        {
            // Implementation: string
            switch (reader.readByteValue())
            {
                case 0:
                    this.name = <any>null;
                    break;
                case 1:
                    this.name = reader.readStringValue();
                    break;
                default:
                    throw new Error('Invalid null code');
            }
        }
        
        // freeArea
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.freeArea = reader.readDoubleValue();
        }
        
        // pressureDrop
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.pressureDrop = reader.readDoubleValue();
        }
        
        // velocity
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.velocity = reader.readDoubleValue();
        }
        
        // volume
        if (reader.readBooleanValue())
        {
            // Implementation: double
            this.volume = reader.readDoubleValue();
        }
    }

    public static parse(hash: unknown): ValidSize {
        // Method Implementation: simple
        const schema = ValidSizeZodSchema.parse(hash);
        return this.parseZodSchema(schema);
    }

    public static parseZodSchema(schema: ValidSizeZodSchemaShape): ValidSize {
        // Method Implementation: parseZodSchema
        return new ValidSize({
            name: schema.name,
            freeArea: schema.freeArea,
            pressureDrop: schema.pressureDrop,
            velocity: schema.velocity,
            volume: schema.volume
        });
    }

    public toJSON(): any {
        // Method Implementation: toJson
        return {
            name: this.isNameDefined ? ((this.name === null ? undefined : this.name)) : undefined,
            freeArea: this.isFreeAreaDefined ? (this.freeArea) : undefined,
            pressureDrop: this.isPressureDropDefined ? (this.pressureDrop) : undefined,
            velocity: this.isVelocityDefined ? (this.velocity) : undefined,
            volume: this.isVolumeDefined ? (this.volume) : undefined,
        };
    }

    public clone(): ValidSize {
        // Method Implementation: clone
        return new ValidSize({
            name: this.isNameDefined ? this.name : undefined,
            freeArea: this.isFreeAreaDefined ? this.freeArea : undefined,
            pressureDrop: this.isPressureDropDefined ? this.pressureDrop : undefined,
            velocity: this.isVelocityDefined ? this.velocity : undefined,
            volume: this.isVolumeDefined ? this.volume : undefined
        });
    }
}

export interface IConfigurationService {
    select(entity: ConfigurationCore.Entity, child: ConfigurationModelEntity.Entity): Promise<void>;

    selectLouvers(entity: ConfigurationCore.Entity): Promise<Selection>;

    selectLouverSizes(entity: ConfigurationCore.Entity, sizeModel: ConfigurationModelEntity.Entity): Promise<ConfigurationCore.Entity[]>;

    createSelectorEntity(): Promise<ConfigurationCore.Entity>;
}

export interface ISelectionService {
    select(selector: Selector): Promise<Selection>;
}

export class ConfigurationServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public select(entity: ConfigurationCore.Entity, child: ConfigurationModelEntity.Entity): Promise<void>;
    public select(actionContext: Wellspring.ActionContext, entity: ConfigurationCore.Entity, child: ConfigurationModelEntity.Entity): Promise<void>;
    public async select(...args: unknown[]): Promise<void> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"ConfigurationCore.Entity"}
            Zod.instanceof(ConfigurationCore.Entity), 
            // {"template":"model","className":"ConfigurationModelEntity.Entity"}
            Zod.instanceof(ConfigurationModelEntity.Entity)
        ]).check(args)) {
            const [ entity, child ] = args;
        
            const activity = new Wellspring.DistributedActivity('configuration.select');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('select', 'utf8'));
                requestMetadata.add('service', Buffer.from('configuration', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("38d8f7fc1acedb91cb01053c5fdf4a8fee98a799404f9d0fa7135ac284e77bb7");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (child && child !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (child === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            child.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                await this.protocol.readTaggedData(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].select`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                child: JSON.parse(JSON.stringify(child))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "configuration/select" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            entity.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `configuration[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        child: JSON.parse(JSON.stringify(child))
                    }
                });
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        child: JSON.parse(JSON.stringify(child))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public selectLouvers(entity: ConfigurationCore.Entity): Promise<Selection>;
    public selectLouvers(actionContext: Wellspring.ActionContext, entity: ConfigurationCore.Entity): Promise<Selection>;
    public async selectLouvers(...args: unknown[]): Promise<Selection> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"ConfigurationCore.Entity"}
            Zod.instanceof(ConfigurationCore.Entity)
        ]).check(args)) {
            const [ entity ] = args;
        
            const activity = new Wellspring.DistributedActivity('configuration.selectLouvers');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('selectLouvers', 'utf8'));
                requestMetadata.add('service', Buffer.from('configuration', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(1);
                    writer.writeStringValue("c59b15e3499a10f6feb4b49fcd4e358d2614065778997bb8b0d0305943fb45d0");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Selection>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].selectLouvers`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "configuration/selectLouvers" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Selection;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Selection();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `configuration[${this.identifier}].selectLouvers`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].selectLouvers`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public selectLouverSizes(entity: ConfigurationCore.Entity, sizeModel?: ConfigurationModelEntity.Entity): Promise<ConfigurationCore.Entity[]>;
    public selectLouverSizes(actionContext: Wellspring.ActionContext, entity: ConfigurationCore.Entity, sizeModel?: ConfigurationModelEntity.Entity): Promise<ConfigurationCore.Entity[]>;
    public async selectLouverSizes(...args: unknown[]): Promise<ConfigurationCore.Entity[]> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.union([
            Zod.tuple([
                // {"template":"model","className":"ConfigurationCore.Entity"}
                Zod.instanceof(ConfigurationCore.Entity)
            ]), 
            Zod.tuple([
                // {"template":"model","className":"ConfigurationCore.Entity"}
                Zod.instanceof(ConfigurationCore.Entity), 
                // {"template":"model","className":"ConfigurationModelEntity.Entity"}
                Zod.instanceof(ConfigurationModelEntity.Entity).optional()
            ])
        ]).check(args)) {
            const [ entity, sizeModel ] = args;
        
            const activity = new Wellspring.DistributedActivity('configuration.selectLouverSizes');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('selectLouverSizes', 'utf8'));
                requestMetadata.add('service', Buffer.from('configuration', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(2);
                    writer.writeStringValue("b0b02325b571d5f199b351c8646d6963b3542628a918cfba42952317ba6f3290");
        
                    if (entity && entity !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (entity === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            entity.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
        
                    if (sizeModel && sizeModel !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (sizeModel === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            sizeModel.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationCore.Entity[]>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].selectLouverSizes`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                entity: JSON.parse(JSON.stringify(entity)),
                                sizeModel: JSON.parse(JSON.stringify(sizeModel))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "configuration/selectLouverSizes" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ConfigurationCore.Entity[];
                    // Implementation: slipstream/read/array
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null arrays');
                            break;
                        case 1:
                            let item: ConfigurationCore.Entity;
                    
                            result = [];
                            
                            const length = reader.readIntegerValue();
                            for (let i = 0; i < length; i++) {
                                // Item Implementation: model
                                switch (reader.readByteValue())
                                {
                                    case 0:
                                        throw new Error('Cannot process null models');
                                        break;
                                    case 1:
                                        item = new ConfigurationCore.Entity();
                                        item.slipstreamRead(reader);
                                        break;
                                    default:
                                        throw new Error('Invalid null code');
                                }
                    
                                result.push(item);
                            }
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `configuration[${this.identifier}].selectLouverSizes`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        sizeModel: JSON.parse(JSON.stringify(sizeModel))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].selectLouverSizes`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        entity: JSON.parse(JSON.stringify(entity)),
                        sizeModel: JSON.parse(JSON.stringify(sizeModel))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }

    public createSelectorEntity(): Promise<ConfigurationCore.Entity>;
    public createSelectorEntity(actionContext: Wellspring.ActionContext): Promise<ConfigurationCore.Entity>;
    public async createSelectorEntity(...args: unknown[]): Promise<ConfigurationCore.Entity> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
        ]).check(args)) {
            const [  ] = args;
        
            const activity = new Wellspring.DistributedActivity('configuration.createSelectorEntity');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('createSelectorEntity', 'utf8'));
                requestMetadata.add('service', Buffer.from('configuration', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(3);
                    writer.writeStringValue("ffe9c31bc2152515de65f509f812a99c82599b0a8f3debc86b7055060fb921e7");
        
                });
        
                const responseData = await this.protocol.readTaggedData<ConfigurationCore.Entity>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].createSelectorEntity`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "configuration/createSelectorEntity" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: ConfigurationCore.Entity;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new ConfigurationCore.Entity();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `configuration[${this.identifier}].createSelectorEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `configuration[${this.identifier}].createSelectorEntity`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class SelectionServiceClient implements Wellspring.IServiceClient {
    public readonly transport: Axon.IClientTransport;

    public readonly protocol: Axon.IProtocol;

    public readonly identifier?: string;

    public constructor(transport: Axon.IClientTransport, protocol: Axon.IProtocol, identifier?: string) {
        // Method Implementation: simple
        this.transport = transport;
        this.protocol = protocol;
        this.identifier = identifier;
    }

    public select(selector: Selector): Promise<Selection>;
    public select(actionContext: Wellspring.ActionContext, selector: Selector): Promise<Selection>;
    public async select(...args: unknown[]): Promise<Selection> {
        // Method Implementation: slipstream/overloadedClientMethod
        const startTime = Date.now();
        
        const actionContext = (args.length && args[0] instanceof Wellspring.ActionContext) ? args.shift() as Wellspring.ActionContext : new Wellspring.ActionContext();
        
        if (Zod.tuple([
            // {"template":"model","className":"Selector"}
            Zod.instanceof(Selector)
        ]).check(args)) {
            const [ selector ] = args;
        
            const activity = new Wellspring.DistributedActivity('selection.select');
            activity.tracers.push(...actionContext.tracers);
            const aid = actionContext.metadata.find('aid')?.toString('utf8');
            if (aid)
                activity.setParentId(aid);
            actionContext.metadata.addOrSet('aid', Buffer.from(activity.id, 'utf8'));
        
            return activity.trace(async () => {
                const messageId = Uuid().replace(/-/g, '').toLowerCase();
        
                const requestMetadata = Axon.VolatileTransportMetadata.fromMetadata(actionContext.metadata);
                requestMetadata.add('action', Buffer.from('select', 'utf8'));
                requestMetadata.add('service', Buffer.from('selection', 'utf8'));
                if (this.identifier)
                    requestMetadata.add('serviceIdentifier', Buffer.from(this.identifier, 'utf8'));
        
                requestMetadata.add('cid', Buffer.from(Uuid().replace(/-/g, '').toLowerCase(), 'utf8'));
        
                await this.protocol.writeTaggedData(this.transport, messageId, requestMetadata, (writer) => {
                    writer.writeShortValue(0);
                    writer.writeStringValue("a4f060931a6f5ecdcbe49cffea68957ff72cb6baf077167977d76e910ffead6b");
        
                    if (selector && selector !== undefined) {
                        writer.writeBooleanValue(true);
                        // Implementation: slipstream/write/model
                        if (selector === null) {
                            writer.writeByteValue(0);
                        }
                        else {
                            writer.writeByteValue(1);
                            selector.slipstreamWrite(writer);
                        }
                    }
                    else {
                        writer.writeBooleanValue(false);
                    }
                });
        
                const responseData = await this.protocol.readTaggedData<Selection>(this.transport, messageId, (reader, metadata) => {
                    for (const frame of metadata.frames)
                        actionContext.metadata.addOrSet(frame.id, frame.data);
        
                    if (!reader.readBooleanValue())
                    {
                        const requestFault = new Wellspring.RequestFault()
                        requestFault.slipstreamRead(reader);
        
                        const endTime = Date.now();
                        console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].select`, {
                            actionContext,
                            duration: endTime - startTime,
                            params: {
                                selector: JSON.parse(JSON.stringify(selector))
                            },
                            requestFault: JSON.parse(JSON.stringify(requestFault))
                        });
        
                        let longMessage = 'Client action request "selection/select" failed';
                        (function parseFault(fault: Wellspring.RequestFault) {
                            longMessage += ' >> ' + fault.message;
                            if (fault.innerFault)
                                parseFault(fault.innerFault);
                        })(requestFault);
        
                        throw new Wellspring.RequestError(longMessage, requestFault);
                    }
        
                    let result: Selection;
                    // Implementation: slipstream/read/model
                    switch (reader.readByteValue())
                    {
                        case 0:
                            throw new Error('Cannot process null models');
                            break;
                        case 1:
                            result = new Selection();
                            result.slipstreamRead(reader);
                            break;
                        default:
                            throw new Error('Invalid null code');
                    }
        
                    return result;
                });
        
                const endTime = Date.now();
                console.log('SERVICE RESPONSE', `selection[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        selector: JSON.parse(JSON.stringify(selector))
                    },
                    response: JSON.parse(JSON.stringify(responseData))
                });
        
                return responseData;
            }).catch(error => {
                const endTime = Date.now();
                console.error('SERVICE RESPONSE [ERROR]', `selection[${this.identifier}].select`, {
                    actionContext,
                    duration: endTime - startTime,
                    params: {
                        selector: JSON.parse(JSON.stringify(selector))
                    },
                    error
                });
        
                throw error;
            });
        }
        else {
            throw new Error('Invalid client method request');
        }
    }
}

export class ModelClassFactory {
    public static createModel(modelName: string) {
        // Method Implementation: createModel
        switch (modelName) {
            case 'Selector':
                return new Selector();
            case 'Selection':
                return new Selection();
            case 'ValidSize':
                return new ValidSize();
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static createFromModel(modelName: string, source: any) {
        // Method Implementation: createFromModel
        switch (modelName) {
            case 'Selector':
                return new Selector(source);
            case 'Selection':
                return new Selection(source);
            case 'ValidSize':
                return new ValidSize(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }

    public static parseModel(modelName: string, source: any) {
        // Method Implementation: parseModel
        switch (modelName) {
            case 'Selector':
                return Selector.parse(source);
            case 'Selection':
                return Selection.parse(source);
            case 'ValidSize':
                return ValidSize.parse(source);
            default:
                throw new Error(`Unknown model ${modelName}`);
        }
    }
}
